<template>
  <v-container fluid>
    <v-row>
      <v-col class="pt-5">
        <h3>Truck Types</h3>
      </v-col>
      <v-col class="text-end">
        <v-btn
          color="primary"
          outlined
          @click="openTruckTypeForm"
        >
          <v-icon
            small
            class="mr-1"
          >
            fas fa-plus
          </v-icon>
          New Truck Type
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-0">
        <v-card
          elevation="0"
          outlined
        >
          <v-data-table
            :headers="truckTypeHeaders"
            :items="truckTypes"
            :footer-props="dataTableFooterProps"
            :items-per-page="20"
            :search="search"
            :loading="truckTypeLoading"
            dense
          >
            <template #[`item.id`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.id }}
              </text-highlight>
            </template>
            <template #[`item.name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.name }}
              </text-highlight>
            </template>
            <template #[`item.weight_limit`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.weight_limit ? item.weight_limit.toLocaleString() : '-' }}
              </text-highlight>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="primary"
                outlined
                small
                @click="openEditTruckTypeForm(item.id)"
              >
                EDIT
              </v-btn>
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="error"
                outlined
                small
                @click="deleteTruckType(item.id)"
              >
                DELETE
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mt-10 d-flex align-center">
        <h3 class="mb-0">Truck Type Links</h3>
        <span class="ml-3 grey--text text--darken-1">
          Selecting one truck type will also reserve its linked truck type for the same delivery request
        </span>
      </v-col>
      <v-col class="text-end mt-8">
        <v-btn
          color="primary"
          outlined
          @click="openTruckTypeLinkedTruckTypeForm"
        >
          <v-icon
            small
            class="mr-1"
          >
            fas fa-plus
          </v-icon>
          New Link
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-0">
        <v-card
          elevation="0"
          outlined
        >
          <v-data-table
            :headers="truckTypeLinkedTruckTypeHeaders"
            :items="truckTypeLinkedTruckTypes"
            :footer-props="dataTableFooterProps"
            :items-per-page="20"
            :search="search"
            :loading="truckTypeLinkedTruckTypeLoading"
            dense
          >
            <template #[`item.id`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.id }}
              </text-highlight>
            </template>
            <template #[`item.truck_type_name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ getTruckTypeName(item.truck_schedule_truck_type_id, item.truck_type) }}
              </text-highlight>
            </template>
            <template #[`item.linked_truck_type_name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ getTruckTypeName(item.linked_truck_schedule_truck_type_id, item.linked_truck_type) }}
              </text-highlight>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="primary"
                outlined
                small
                @click="openEditTruckTypeLinkedTruckTypeForm(item)"
              >
                EDIT
              </v-btn>
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="error"
                outlined
                small
                @click="deleteTruckTypeLinkedTruckType(item.id)"
              >
                DELETE
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mt-10">
        <h3>Trucks</h3>
      </v-col>
      <v-col class="text-end mt-8">
        <v-btn
          color="primary"
          outlined
          @click="openTruckForm"
        >
          <v-icon
            small
            class="mr-1"
          >
            fas fa-plus
          </v-icon>
          New Truck
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-0">
        <v-card
          elevation="0"
          outlined
        >
          <v-data-table
            :headers="truckHeaders"
            :items="trucks"
            :footer-props="dataTableFooterProps"
            :items-per-page="20"
            :search="search"
            :loading="truckLoading"
            dense
          >
            <template #[`item.id`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.id }}
              </text-highlight>
            </template>
            <template #[`item.name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.name }}
              </text-highlight>
            </template>
            <template #[`item.truck_type`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.truck_type }}
              </text-highlight>
            </template>
            <template #[`item.type_weight_limit`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.type_weight_limit ? item.type_weight_limit.toLocaleString() : '-' }}
              </text-highlight>
            </template>
            <template #[`item.plate_number`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.plate_number }}
              </text-highlight>
            </template>
            <template #[`item.truck_number`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.truck_number }}
              </text-highlight>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="primary"
                outlined
                small
                @click="openEditTruckForm(item.id)"
              >
                EDIT
              </v-btn>
              <v-btn
                class="ml-1 mt-1 mb-1"
                color="error"
                outlined
                small
                @click="deleteTruck(item.id)"
              >
                DELETE
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <truck-schedule-truck-form
      ref="truckForm"
      @success="getTrucks"
    />
    <truck-schedule-truck-type-form
      ref="truckTypeForm"
      @success="getTruckTypes"
    />
    <truck-schedule-truck-type-linked-truck-type-form
      ref="truckTypeLinkedTruckTypeForm"
      :truck-types="truckTypes"
      @success="getTruckTypeLinkedTruckTypes"
    />
    <confirm-form ref="confirmForm" />
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import TruckScheduleTruckForm from '@/components/forms/truckSchedules/TruckScheduleTruckForm';
import TruckScheduleTruckTypeForm from '@/components/forms/truckSchedules/TruckScheduleTruckTypeForm';
import TruckScheduleTruckTypeLinkedTruckTypeForm from '@/components/forms/truckSchedules/TruckScheduleTruckTypeLinkedTruckTypeForm';

export default {
  components: {
    TruckScheduleTruckForm,
    TruckScheduleTruckTypeForm,
    TruckScheduleTruckTypeLinkedTruckTypeForm,
  },
  props: {

  },
  data: () => ({
    truckTypes: [],
    truckTypeLinkedTruckTypes: [],
    trucks: [],
    truckTypeLoading: false,
    truckTypeLinkedTruckTypeLoading: false,
    truckLoading: false,
    truckTypeHeaders: [
      { text: 'Type#', value: 'id' },
      { text: 'Name', value: 'name' },
      { text: 'Weight Limit (kg)', value: 'weight_limit' },
      { text: 'Color', value: 'color' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Created By', value: 'user_full_name' },
      {
        text: 'Actions', value: 'actions', width: '15%', align: 'end',
      },
    ],
    dataTableFooterProps: {
      'items-per-page-options': [20, 50, 100, -1],
    },
    truckTypeLinkedTruckTypeHeaders: [
      { text: 'Link#', value: 'id' },
      { text: 'Truck Type', value: 'truck_type_name' },
      { text: 'Linked Truck Type', value: 'linked_truck_type_name' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Created By', value: 'user_full_name' },
      {
        text: 'Actions', value: 'actions', width: '15%', align: 'end',
      },
    ],
    truckHeaders: [
      { text: 'Truck#', value: 'id' },
      { text: 'Name', value: 'name' },
      { text: 'Truck Type', value: 'type_name' },
      { text: 'Weight Limit (kg)', value: 'type_weight_limit' },
      { text: 'Plate Number', value: 'plate_number' },
      { text: 'Truck Number', value: 'truck_number' },
      { text: 'Color', value: 'color' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Created By', value: 'user_full_name' },
      {
        text: 'Actions', value: 'actions', width: '15%', align: 'end',
      },
    ],
  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
    }),
    ...mapGetters([

    ]),
  },
  watch: {

  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      await this.getTruckTypes();
      this.getTruckTypeLinkedTruckTypes();
      this.getTrucks();
    },

    async getTruckTypes() {
      const submitModel = {
        filters: {
        },
      };
      this.truckTypes = await this.$api.truckScheduleTruckType.read(submitModel);
    },

    async getTruckTypeLinkedTruckTypes() {
      const submitModel = {
        filters: {
        },
      };
      this.truckTypeLinkedTruckTypeLoading = true;
      const res = await this.$api.truckScheduleTruckTypeLinkedTruckType.read(submitModel);
      if (!res) {
        this.truckTypeLinkedTruckTypeLoading = false;
        this.$bus.$emit('showError', 'Failed to load linked truck types.');
        return;
      }
      const truckTypeLinkedTruckTypes = Array.isArray(res) ? res : [];
      this.truckTypeLinkedTruckTypes = truckTypeLinkedTruckTypes.map((item) => ({
        ...item,
        truck_type_name: this.getTruckTypeName(item.truck_schedule_truck_type_id, item.truck_type),
        linked_truck_type_name: this.getTruckTypeName(item.linked_truck_schedule_truck_type_id, item.linked_truck_type),
      }));
      this.truckTypeLinkedTruckTypeLoading = false;
    },

    async getTrucks() {
      const submitModel = {
        filters: {
        },
      };
      this.trucks = await this.$api.truckScheduleTruck.read(submitModel);
    },

    openTruckTypeForm() {
      this.$refs.truckTypeForm.open();
    },

    openEditTruckTypeForm(id) {
      this.$refs.truckTypeForm.open(id);
    },

    openTruckTypeLinkedTruckTypeForm() {
      this.$refs.truckTypeLinkedTruckTypeForm.open();
    },

    openEditTruckTypeLinkedTruckTypeForm(item) {
      this.$refs.truckTypeLinkedTruckTypeForm.open(item);
    },

    openTruckForm() {
      this.$refs.truckForm.open();
    },

    openEditTruckForm(id) {
      this.$refs.truckForm.open(id);
    },

    async deleteTruckType(id) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to delete this record?',
      });
      if (!confirm) return;
      const res = await this.$api.truckScheduleTruckType.delete(id);
      if (!res) return;
      this.$bus.$emit('showSuccess');
      await this.getTruckTypes();
      this.getTruckTypeLinkedTruckTypes();
    },

    async deleteTruckTypeLinkedTruckType(id) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to delete this record?',
      });
      if (!confirm) return;
      try {
        await this.$api.truckScheduleTruckTypeLinkedTruckType.delete(id);
        this.$bus.$emit('showSuccess');
        this.getTruckTypeLinkedTruckTypes();
      } catch (error) {
        this.$bus.$emit('showError', error.message || 'Unable to delete truck type link');
      }
    },

    async deleteTruck(id) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to delete this record?',
      });
      if (!confirm) return;
      const res = await this.$api.truckScheduleTruck.delete(id);
      if (!res) return;
      this.$bus.$emit('showSuccess');
      this.getTrucks();
    },

    getTruckTypeName(truckTypeId, truckType = null) {
      if (truckType && truckType.name) return truckType.name;
      const selectedTruckType = this.truckTypes.find((item) => item.id == truckTypeId);
      return selectedTruckType ? selectedTruckType.name : '';
    },
  },
};
</script>

<style scoped>

</style>
